import { useEffect, useMemo, useState } from 'react';

import type { AbstractToken, Tokens } from '@okta/okta-auth-js';
import { useOktaAuth } from '@okta/okta-react';
import { useMutation } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { useLocation } from 'react-router';

import config from '../config';
import { isMagicLinkUserAtom } from '../modules/login/atoms';
import {
  makeAPIRequestReactQuery,
  makeAPIRequestReactQueryWithHeaders,
} from '../react-query/api';
import { useTimer } from '../utils/hooks/useTimer';
import { mixpanelEvents, useTrackEvent } from '../utils/mixpanel';

export const MAGIC_URL_PATHNAME = '/magic';

export const useGetMagicLink = () => {
  const oktaAuth = useOktaAuth().oktaAuth || null;
  const location = useLocation();
  const [key, setKey] = useState<string | null>(null);
  const [tokens, setTokens] = useState<null | (Tokens & Partial<SessionToken>)>(
    null,
  );
  /**
   * 'sessionLoading' is used to track the auth flow and surface this to upstream consumers
   *  of the hook where logic is dependent on the presence of a session token
   */
  const [isSessionLoading, setIsSessionLoading] = useState(false);
  /**
   * 'tokensPresent' is used to determine if we need to set access/id tokens in tokenManager
   * these tokens may already be set if the session is active and in that case
   * we don't want to generate/store them again.
   */
  const [isTokensPresent, setIsTokensPresent] = useState(false);

  const path = useMemo(() => `${config.API_URL}/v2/auth/magic/${key}`, [key]);
  const validatePath = useMemo(
    () => `${config.API_URL}/auth/magic/validate/${key}`,
    [key],
  );

  const { mutateAsync } = useMutation(
    [path],
    () => makeAPIRequestReactQuery<SessionToken>(path),
    {},
  );

  const { mutateAsync: validateSession } = useMutation(
    [validatePath],
    () =>
      makeAPIRequestReactQueryWithHeaders<boolean>(
        validatePath,
        {},
        { Authorization: `Bearer ${oktaAuth.getAccessToken()}` },
      ),
    {},
  );

  const { trackEvent } = useTrackEvent();
  const setIsMagicLinkUser = useSetAtom(isMagicLinkUserAtom);
  const { start, getTimePassed } = useTimer();

  const search = new URLSearchParams(location.search);
  const returnUrl = search.get('returnUrl') ?? '';
  useEffect(() => {
    if (location.pathname !== MAGIC_URL_PATHNAME) {
      return;
    }
    start();
    const magicKey = search.get('key');
    setIsMagicLinkUser(true);

    setKey(magicKey?.replace(' ', '') ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, mutateAsync, setIsMagicLinkUser, returnUrl]);

  function retrieveSession() {
    mutateAsync()
      .then((tok) => {
        // if tok not set, user will be redirected to login
        if (!tok) {
          trackEvent(mixpanelEvents.magicLinkRedirectToLogin, {
            err: 'Retrieved null session token',
          });
          setTokens({});
          return;
        }
        oktaAuth.token
          .getWithoutPrompt({
            responseType: ['id_token', 'token'],
            sessionToken: tok.sessionToken,
            prompt: 'login',
          })
          .then((res) => {
            setTokens({
              ...res.tokens,
            });
          })
          .catch((err) => {
            trackEvent(mixpanelEvents.magicLinkFailedToFetchTokens, {
              err,
            });
          });
      })
      .catch((err) => {
        // handle any nucleus errors by returning user to login
        trackEvent(mixpanelEvents.magicLinkRedirectToLogin, {
          err,
        });
        setTokens({});
        return;
      });
  }

  function loginExistingSession() {

    if (!oktaAuth.getAccessToken() || !oktaAuth.getIdToken()) {
      oktaAuth.token
        .getWithoutPrompt({
          responseType: ['id_token', 'token'],
          prompt: 'login',
        })
        .then((res) => {
          setTokens({
            ...res.tokens,
          });
        })
        .catch((err) => {
          trackEvent(mixpanelEvents.magicLinkFailedToFetchTokens, {
            err,
          });
        });
    } else {
      setTokens({});
      setIsTokensPresent(true);
    }
  }

  function logoutExistingAndRefresh() {
    oktaAuth.tokenManager.clear();
    oktaAuth.session
      .close()
      .then(() => {
        retrieveSession();
      })
      .catch((err) => {
        // we shouldn't hit this case, but make sure user goes to login if so
        trackEvent(mixpanelEvents.magicLinkRedirectToLogin, {
          err,
        });
        setTokens({});
        setIsTokensPresent(true);
      });
  }

  // main landing onto magic link
  useEffect(() => {
    if (!key) {
      return;
    }
    setIsSessionLoading(true);
    oktaAuth.session
      .exists()
      .then((exists) => {
        if (!exists) {
          retrieveSession();
        } else {
          // validate user's session against the magic link they are accessing
          validateSession()
            .then((validated) => {
              if (validated) {
                loginExistingSession();
              } else {
                logoutExistingAndRefresh();
              }
            })
            .catch(() => {
              logoutExistingAndRefresh();
            });
        }
      })
      .catch(() => {
        // okta may throw a 404 when checking session, so handle that gracefully
        retrieveSession();
      });
    setIsSessionLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, oktaAuth.token, mutateAsync]);

  return {
    isSessionLoading,
    tokens,
    isTokensPresent,
    redirect: returnUrl,
  };
};

export interface SessionToken extends AbstractToken {
  sessionToken: string;
}
